import { useContext, useEffect, useRef } from 'react';
import {
  SupportContext,
  type SupportContextValue,
} from '../contexts/supportContextValue';

const noop = () => {};

/**
 * Read the current support context. Used by the SupportModal.
 */
export function useSupportContext(): SupportContextValue {
  const state = useContext(SupportContext);
  if (!state) {
    return {};
  }
  return state.context;
}

/**
 * Modal open/close wiring. Only the modal itself needs all three — everyone
 * else wants `useOpenSupport`.
 */
export function useSupportModal(): {
  isOpen: boolean;
  openSupport: (ctx?: SupportContextValue) => void;
  closeSupport: () => void;
} {
  const state = useContext(SupportContext);
  return {
    isOpen: state?.isOpen ?? false,
    openSupport: state?.openSupport ?? noop,
    closeSupport: state?.closeSupport ?? noop,
  };
}

/**
 * Open the Contact Support modal from anywhere under the provider — e.g. from
 * an error message offering support as the next step. Pass one-shot context to
 * pre-fill the request; it is layered over the page's own support context and
 * dropped when the modal closes.
 */
export function useOpenSupport(): (ctx?: SupportContextValue) => void {
  return useSupportModal().openSupport;
}

/**
 * Set support context for the current page. Automatically clears on unmount.
 * Call this near the top of page components to provide screen-specific context.
 */
export function useSetSupportContext(ctx: SupportContextValue): void {
  const state = useContext(SupportContext);
  // Serialize only JSON-safe fields for change detection (label may be ReactNode)
  const serialized = JSON.stringify({
    subject: ctx.subject,
    hiddenContext: ctx.hiddenContext,
    recipient: ctx.recipient,
  });

  // Keep stable refs so the effect only depends on serialized content, not
  // the context state object (which changes on every setContext call).
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    stateRef.current?.setContext(ctxRef.current);
    return () => {
      stateRef.current?.clearContext();
    };
  }, [serialized]);
}
